import { NgOptimizedImage, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  PLATFORM_ID,
  Signal,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationEnd,
  Params,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
  RoutesRecognized,
} from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { filter } from 'rxjs';
import { environment } from '../environments/environment';
import { LOCALE_KEY, MAIN_LINKS, QR, SupportedLocales, WAYTR } from './constants';
import { PagesEnum } from './enums';
import { stopPropagation, updateLinks } from './helpers';
import { ISessionParams } from './interfaces';
import { AppLinksTypeModel } from './models/types';
import { FingerprintService, LocalStorageService, LocalesService, RouterStateService, SentryService, TitleService } from './services';
import { SessionStateService } from './services/session-state.service';
import {
  AppMenuBarComponent,
  AppMenuIconComponent,
  CookieBannerComponent,
  WaytrIconsService,
  waytrAdmin,
  waytrAnalytics,
  waytrArrowDown,
  waytrArrowLeft,
  waytrArrowRight,
  waytrBowl,
  waytrBreakfast,
  waytrBurger,
  waytrBusy,
  waytrCard,
  waytrCart,
  waytrChicken,
  waytrClean,
  waytrClose,
  waytrCutlery,
  waytrDirectSale,
  waytrDrink,
  waytrEdit,
  waytrFolder,
  waytrFoodMenu,
  waytrFresh,
  waytrHash,
  waytrLink,
  waytrLink2,
  waytrMushrooms,
  waytrNoodle,
  waytrNotification,
  waytrOrders,
  waytrPastry,
  waytrPizza,
  waytrSales,
  waytrSaltAndPepper,
  waytrSearch,
  waytrSoup,
  waytrStar,
  waytrSushi,
  waytrSweet,
  waytrTable,
  waytrTableManagement,
  waytrTaco,
  waytrTissue,
  waytrUpload,
  waytrWaiter,
} from './shared';

@Component({
  selector: 'app-root',
  standalone: true,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    NgOptimizedImage,
    NzSkeletonModule,
    AppMenuBarComponent,
    AppMenuIconComponent,
    CookieBannerComponent,
    TranslateModule,
  ],
})
export class AppComponent implements OnInit {
  readonly #router = inject(Router);
  readonly #routerStateService = inject(RouterStateService);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #sessionStateService = inject(SessionStateService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #titleService = inject(TitleService);
  // eslint-disable-next-line no-unused-private-class-members
  readonly #translateService = inject(TranslateService); // Side effect: configures translations
  // eslint-disable-next-line no-unused-private-class-members
  readonly #cdr = inject(ChangeDetectorRef); // Reserved for future use
  // eslint-disable-next-line no-unused-private-class-members
  readonly #platformId = inject(PLATFORM_ID); // Reserved for SSR platform checks
  // eslint-disable-next-line no-unused-private-class-members
  readonly #sentryService = inject(SentryService); // Side effect: initializes Sentry based on consent

  protected pages = PagesEnum;
  protected stopPropagation = stopPropagation;

  protected appMenuBarShown: Signal<boolean> = this.#routerStateService.appMenuBarShown;
  protected routingParams: Signal<ISessionParams | undefined> = this.#routerStateService.routingParams;

  protected navigationLinks: Signal<AppLinksTypeModel[]> = computed(() => this.calculateNavigationLinks(this.routingParams()));
  protected zoomed: WritableSignal<boolean> = signal(true);
  protected elementLoaded = true;

  private lastDetailedLog?: number;

  constructor() {
    // Initialize memory monitoring in development
    if (!environment.production) {
      this.initializeMemoryMonitoring();
    }
  }

  ngOnInit() {
    this.#sessionStateService.createAppState();

    // Handle route changes with better state management
    this.#router.events
      .pipe(
        filter(event => event instanceof RoutesRecognized),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(event => {
        if (event instanceof RoutesRecognized) {
          this.handleRouteChange(event);
        }
      });

    // Handle navigation end to reset zoom state
    this.#router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        this.zoomed.set(true);
        const params = this.#activatedRoute.firstChild?.snapshot.params;
        if (params) {
          this.setEventRoutingParams(params);
        }
      });
  }

  private handleRouteChange(event: RoutesRecognized): void {
    const data = event.state.root.firstChild?.data;
    const title: string = data?.['title'];

    if (title) {
      this.#titleService.changeToNewPage(title);
    }
  }

  private calculateNavigationLinks(routingParams: ISessionParams | undefined): AppLinksTypeModel[] {
    return routingParams ? updateLinks(MAIN_LINKS, this.routingParams()) : MAIN_LINKS;
  }

  private setEventRoutingParams(params: Params): void {
    const waytrSessionParams = {
      venue: params.venue,
      table: params.table,
      sessionUrl: params.sessionUrl ?? 'menu',
    };

    this.#routerStateService.setRoutingParams(waytrSessionParams);
    this.#routerStateService.showAppMenuBar();
  }

  protected zoomInApp(event: MouseEvent | TouchEvent | KeyboardEvent): void {
    this.stopPropagation(event);
    this.zoomed.set(true);
  }

  protected toggleZoom(event: MouseEvent | TouchEvent | KeyboardEvent): void {
    this.stopPropagation(event);
    this.zoomed.set(!this.zoomed());
  }

  /**
   * Initialize memory monitoring in development environment
   * Logs memory stats at regular intervals for performance monitoring
   */
  private initializeMemoryMonitoring(): void {
    // Check if Performance.memory API is available
    const hasMemoryAPI = (performance as { memory?: unknown }).memory !== undefined;

    if (!hasMemoryAPI) {
      console.info('[App] Memory API not available in this browser');
      return;
    }

    console.log('[App] Memory monitoring enabled for development');

    // Log memory stats every 60 seconds in development (reduced frequency)
    setInterval(() => {
      const memory = (performance as { memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number; externalMemoryUsage?: number } })
        .memory;

      if (memory) {
        const heapUsedMB = (memory.usedJSHeapSize / 1048576).toFixed(2);
        const heapLimitMB = (memory.jsHeapSizeLimit / 1048576).toFixed(2);
        const heapUsagePercent = ((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100).toFixed(1);

        console.log(
          `[Memory] Heap: ${heapUsedMB}MB / ${heapLimitMB}MB (${heapUsagePercent}%) - ` +
            `Total: ${(memory.totalJSHeapSize / 1048576).toFixed(2)}MB`,
        );

        // Log detailed info every 5 minutes for debugging
        const now = Date.now();
        if (!this.lastDetailedLog || now - this.lastDetailedLog > 300000) {
          // 5 minutes
          console.group('[Memory] Detailed Analysis');
          console.log('Compare with Chrome DevTools Memory tab for accuracy');
          console.log('JS Heap Used:', `${heapUsedMB}MB`);
          console.log('JS Heap Limit:', `${heapLimitMB}MB`);
          console.log('Total JS Heap:', `${(memory.totalJSHeapSize / 1048576).toFixed(2)}MB`);
          if (memory.externalMemoryUsage) {
            console.log('External Memory:', `${(memory.externalMemoryUsage / 1048576).toFixed(2)}MB`);
          }
          console.groupEnd();
          this.lastDetailedLog = now;
        }
      }
    }, 60000); // Every 60 seconds
  }
}

export function appInitializerFactory(
  titleService: TitleService,
  localesService: LocalesService,
  fingerprintService: FingerprintService,
  localStorageService: LocalStorageService,
  translateService: TranslateService,
  platformId: string | object,
): () => Promise<void> {
  return async () => {
    // Inject WaytrIconsService directly within the factory using inject()
    const waytrIconsService = inject(WaytrIconsService);

    // Defer icon registration to idle time to avoid blocking initial render
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(
        () => {
          waytrIconsService.addIcons([
            waytrFresh,
            waytrBowl,
            waytrCart,
            waytrChicken,
            waytrCutlery,
            waytrMushrooms,
            waytrPastry,
            waytrPizza,
            waytrSweet,
            waytrOrders,
            waytrFoodMenu,
            waytrTableManagement,
            waytrWaiter,
            waytrDirectSale,
            waytrTable,
            waytrFolder,
            waytrAnalytics,
            waytrNotification,
            waytrBusy,
            waytrStar,
            waytrSales,
            waytrHash,
            waytrLink,
            waytrCard,
            waytrUpload,
            waytrEdit,
            waytrLink2,
            waytrTaco,
            waytrNoodle,
            waytrBreakfast,
            waytrSoup,
            waytrSushi,
            waytrBurger,
            waytrDrink,
            waytrAdmin,
            waytrSearch,
            waytrSaltAndPepper,
            waytrTissue,
            waytrClean,
            waytrClose,
            waytrArrowDown,
            waytrArrowLeft,
            waytrArrowRight,
          ]);
        },
        { timeout: 2000 },
      );
    } else {
      // Fallback for browsers that don't support requestIdleCallback
      setTimeout(() => {
        waytrIconsService.addIcons([
          waytrFresh,
          waytrBowl,
          waytrCart,
          waytrChicken,
          waytrCutlery,
          waytrMushrooms,
          waytrPastry,
          waytrPizza,
          waytrSweet,
          waytrOrders,
          waytrFoodMenu,
          waytrTableManagement,
          waytrWaiter,
          waytrDirectSale,
          waytrTable,
          waytrFolder,
          waytrAnalytics,
          waytrNotification,
          waytrBusy,
          waytrStar,
          waytrSales,
          waytrHash,
          waytrLink,
          waytrCard,
          waytrUpload,
          waytrEdit,
          waytrLink2,
          waytrTaco,
          waytrNoodle,
          waytrBreakfast,
          waytrSoup,
          waytrSushi,
          waytrBurger,
          waytrDrink,
          waytrAdmin,
          waytrSearch,
          waytrSaltAndPepper,
          waytrTissue,
          waytrClean,
          waytrClose,
          waytrArrowDown,
          waytrArrowLeft,
          waytrArrowRight,
        ]);
      }, 100);
    }

    if (isPlatformBrowser(platformId)) {
      // Initialize device fingerprint for device identification (non-blocking)
      fingerprintService.initializeFingerprint();

      const localesPromises = await Promise.all([localStorageService.getData<string>(LOCALE_KEY), localesService.getAllLocales()]);
      const [storedLocale, allLocales] = localesPromises;

      const locale = storedLocale ?? SupportedLocales.RO_RO;

      titleService.loadInitialTitle(`${WAYTR} ${QR}`);
      translateService.setDefaultLang(locale);
      localesService.setLocale(locale as SupportedLocales);
      localesService.setAllLocales(allLocales);
    }
  };
}
